export interface Fee {
  id: string;
  name: string;
  amount: number;
  unit: string;
  isEstimated?: boolean;
}

export interface YearlyData {
  year: number;
  waterFee: number;
  sewageFee: number;
  wasteFee: number;
  propertyTax: number;
  chimneySweepFee: number;
  totalFees: number;
  isEstimated?: boolean;
  /** Hvilke avgifter som er estimert (fylt fra forrige år) */
  estimatedWater?: boolean;
  estimatedSewage?: boolean;
  estimatedWaste?: boolean;
  estimatedProperty?: boolean;
  estimatedChimney?: boolean;
}

export interface Municipality {
  id: string;
  name: string;
  county: string;
  population: number;
  currentFees: Fee[];
  historicalData: YearlyData[];
  ranking?: number; // Plassering på landsbasis (1 = dyreste)
}

export interface FeeCategory {
  id: string;
  name: string;
}

/** Alle avgiftskategorier appen viser */
export const FEE_CATEGORIES: FeeCategory[] = [
  { id: 'water', name: 'Vann' },
  { id: 'sewage', name: 'Avløp' },
  { id: 'waste', name: 'Renovasjon' },
  { id: 'property', name: 'Septiktømming' },
  { id: 'chimney', name: 'Feiing og tilsyn' },
];

/** Default valgte kategorier – septiktømming er av by default */
export const DEFAULT_ENABLED_CATEGORIES: string[] = FEE_CATEGORIES
  .filter(c => c.id !== 'property')
  .map(c => c.id);

export const CATEGORY_FIELD: Record<string, keyof YearlyData> = {
  water: 'waterFee',
  sewage: 'sewageFee',
  waste: 'wasteFee',
  property: 'propertyTax',
  chimney: 'chimneySweepFee',
};

/** Summer kun de valgte kategoriene fra et års rådata */
export const getYearlyTotal = (yearData: YearlyData, enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES): number =>
  enabledCategories.reduce((sum, id) => sum + (Number(yearData[CATEGORY_FIELD[id]]) || 0), 0);

/** Summer kun de valgte kategoriene fra en liste med Fee-objekter */
export const sumFees = (fees: Fee[], enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES): number =>
  fees.filter(fee => enabledCategories.includes(fee.id)).reduce((sum, fee) => sum + fee.amount, 0);

/** Filtrer en liste med Fee-objekter til kun de valgte kategoriene */
export const filterFees = (fees: Fee[], enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES): Fee[] =>
  fees.filter(fee => enabledCategories.includes(fee.id));

/** Hent avgifter for et gitt år fra kommunens historiske data */
export const getFeesForYear = (municipality: Municipality, year: number): Fee[] => {
  const yearData = municipality.historicalData.find(d => d.year === year);
  if (!yearData) return municipality.currentFees;
  return [
    { id: 'water', name: 'Vann', amount: yearData.waterFee, unit: 'kr/år', isEstimated: yearData.estimatedWater },
    { id: 'sewage', name: 'Avløp', amount: yearData.sewageFee, unit: 'kr/år', isEstimated: yearData.estimatedSewage },
    { id: 'waste', name: 'Renovasjon', amount: yearData.wasteFee, unit: 'kr/år', isEstimated: yearData.estimatedWaste },
    { id: 'property', name: 'Septiktømming', amount: yearData.propertyTax, unit: 'kr/år', isEstimated: yearData.estimatedProperty },
    { id: 'chimney', name: 'Feiing og tilsyn', amount: yearData.chimneySweepFee, unit: 'kr/år', isEstimated: yearData.estimatedChimney }
  ];
};

/** Hent YearlyData for et gitt år */
export const getYearlyDataForYear = (municipality: Municipality, year: number): YearlyData | undefined =>
  municipality.historicalData.find(d => d.year === year);

/** Prosentvis endring fra forrige år. Returnerer null hvis data mangler. */
export const getYearOverYearChange = (
  municipality: Municipality,
  year: number,
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): number | null => {
  const current = getYearlyDataForYear(municipality, year);
  const previous = getYearlyDataForYear(municipality, year - 1);
  if (!current || !previous) return null;
  const previousTotal = getYearlyTotal(previous, enabledCategories);
  if (previousTotal <= 0) return null;
  return ((getYearlyTotal(current, enabledCategories) - previousTotal) / previousTotal) * 100;
};

// Fallback mock data to use if the API fails
const mockMunicipalities: Municipality[] = [
  {
    id: "0301",
    name: "Oslo",
    county: "Oslo",
    population: 698660,
    currentFees: [
      { id: "water", name: "Vann", amount: 3850, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 4250, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3980, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 4500, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 3200, sewageFee: 3600, wasteFee: 3400, propertyTax: 4000, chimneySweepFee: 450, totalFees: 14650 },
      { year: 2020, waterFee: 3400, sewageFee: 3800, wasteFee: 3600, propertyTax: 4100, chimneySweepFee: 450, totalFees: 15350 },
      { year: 2021, waterFee: 3600, sewageFee: 4000, wasteFee: 3800, propertyTax: 4200, chimneySweepFee: 450, totalFees: 16050 },
      { year: 2022, waterFee: 3700, sewageFee: 4100, wasteFee: 3900, propertyTax: 4300, chimneySweepFee: 450, totalFees: 16450 },
      { year: 2023, waterFee: 3850, sewageFee: 4250, wasteFee: 3980, propertyTax: 4500, chimneySweepFee: 450, totalFees: 17030 }
    ]
  },
  {
    id: "1103",
    name: "Stavanger",
    county: "Rogaland",
    population: 144970,
    currentFees: [
      { id: "water", name: "Vann", amount: 2950, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 3850, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3320, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 3800, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 2500, sewageFee: 3200, wasteFee: 2900, propertyTax: 3400, chimneySweepFee: 450, totalFees: 12450 },
      { year: 2020, waterFee: 2600, sewageFee: 3400, wasteFee: 3000, propertyTax: 3500, chimneySweepFee: 450, totalFees: 12950 },
      { year: 2021, waterFee: 2750, sewageFee: 3600, wasteFee: 3100, propertyTax: 3600, chimneySweepFee: 450, totalFees: 13500 },
      { year: 2022, waterFee: 2850, sewageFee: 3750, wasteFee: 3200, propertyTax: 3700, chimneySweepFee: 450, totalFees: 13950 },
      { year: 2023, waterFee: 2950, sewageFee: 3850, wasteFee: 3320, propertyTax: 3800, chimneySweepFee: 450, totalFees: 14370 }
    ]
  },
  {
    id: "5001",
    name: "Trondheim",
    county: "Trøndelag",
    population: 205163,
    currentFees: [
      { id: "water", name: "Vann", amount: 3250, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 4150, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3750, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 3950, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 2700, sewageFee: 3500, wasteFee: 3200, propertyTax: 3500, chimneySweepFee: 450, totalFees: 13350 },
      { year: 2020, waterFee: 2850, sewageFee: 3700, wasteFee: 3350, propertyTax: 3650, chimneySweepFee: 450, totalFees: 14000 },
      { year: 2021, waterFee: 3000, sewageFee: 3900, wasteFee: 3500, propertyTax: 3750, chimneySweepFee: 450, totalFees: 14600 },
      { year: 2022, waterFee: 3100, sewageFee: 4000, wasteFee: 3600, propertyTax: 3850, chimneySweepFee: 450, totalFees: 15000 },
      { year: 2023, waterFee: 3250, sewageFee: 4150, wasteFee: 3750, propertyTax: 3950, chimneySweepFee: 450, totalFees: 15550 }
    ]
  },
  {
    id: "4601",
    name: "Bergen",
    county: "Vestland",
    population: 283930,
    currentFees: [
      { id: "water", name: "Vann", amount: 3650, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 4350, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3850, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 4200, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 3100, sewageFee: 3800, wasteFee: 3300, propertyTax: 3800, chimneySweepFee: 450, totalFees: 14450 },
      { year: 2020, waterFee: 3250, sewageFee: 3950, wasteFee: 3450, propertyTax: 3900, chimneySweepFee: 450, totalFees: 15000 },
      { year: 2021, waterFee: 3400, sewageFee: 4100, wasteFee: 3600, propertyTax: 4000, chimneySweepFee: 450, totalFees: 15550 },
      { year: 2022, waterFee: 3550, sewageFee: 4200, wasteFee: 3750, propertyTax: 4100, chimneySweepFee: 450, totalFees: 16050 },
      { year: 2023, waterFee: 3650, sewageFee: 4350, wasteFee: 3850, propertyTax: 4200, chimneySweepFee: 450, totalFees: 16500 }
    ]
  },
  {
    id: "3004",
    name: "Fredrikstad",
    county: "Viken",
    population: 82385,
    currentFees: [
      { id: "water", name: "Vann", amount: 3150, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 4050, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3550, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 3600, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 2600, sewageFee: 3400, wasteFee: 3000, propertyTax: 3100, chimneySweepFee: 450, totalFees: 12550 },
      { year: 2020, waterFee: 2750, sewageFee: 3600, wasteFee: 3150, propertyTax: 3250, chimneySweepFee: 450, totalFees: 13200 },
      { year: 2021, waterFee: 2900, sewageFee: 3800, wasteFee: 3300, propertyTax: 3400, chimneySweepFee: 450, totalFees: 13850 },
      { year: 2022, waterFee: 3050, sewageFee: 3950, wasteFee: 3450, propertyTax: 3500, chimneySweepFee: 450, totalFees: 14400 },
      { year: 2023, waterFee: 3150, sewageFee: 4050, wasteFee: 3550, propertyTax: 3600, chimneySweepFee: 450, totalFees: 14800 }
    ]
  },
  {
    id: "3803",
    name: "Tønsberg",
    county: "Vestfold og Telemark",
    population: 56293,
    currentFees: [
      { id: "water", name: "Vann", amount: 3050, unit: "kr/år" },
      { id: "sewage", name: "Avløp", amount: 3950, unit: "kr/år" },
      { id: "waste", name: "Renovasjon", amount: 3450, unit: "kr/år" },
      { id: "property", name: "Septiktømming", amount: 3700, unit: "kr/år" },
      { id: "chimney", name: "Feiing og tilsyn", amount: 450, unit: "kr/år" }
    ],
    historicalData: [
      { year: 2019, waterFee: 2500, sewageFee: 3300, wasteFee: 2900, propertyTax: 3200, chimneySweepFee: 450, totalFees: 12350 },
      { year: 2020, waterFee: 2650, sewageFee: 3500, wasteFee: 3050, propertyTax: 3350, chimneySweepFee: 450, totalFees: 13000 },
      { year: 2021, waterFee: 2800, sewageFee: 3700, wasteFee: 3200, propertyTax: 3500, chimneySweepFee: 450, totalFees: 13650 },
      { year: 2022, waterFee: 2950, sewageFee: 3850, wasteFee: 3350, propertyTax: 3600, chimneySweepFee: 450, totalFees: 14200 },
      { year: 2023, waterFee: 3050, sewageFee: 3950, wasteFee: 3450, propertyTax: 3700, chimneySweepFee: 450, totalFees: 14600 }
    ]
  },
];

// Cache for municipalities data
let municipalitiesCache: Municipality[] | null = null;
let allMunicipalitiesCache: Municipality[] | null = null;

export const getMunicipalities = async (): Promise<Municipality[]> => {
  try {
    if (municipalitiesCache) {
      return municipalitiesCache;
    }
    
    // Import dynamically to avoid circular dependencies
    const { fetchMunicipalFees } = await import('@/services/ssbService');
    const data = await fetchMunicipalFees();
    
    // If we got data from the API, cache it and return
    if (data && data.length > 0) {
      municipalitiesCache = data;
      return data;
    }
    
    // Fallback to mock data if the API call failed or returned empty data
    console.warn('Using mock data because API call failed or returned empty data');
    municipalitiesCache = mockMunicipalities;
    return mockMunicipalities;
  } catch (error) {
    console.error('Error getting municipalities:', error);
    municipalitiesCache = mockMunicipalities;
    return mockMunicipalities;
  }
};

export const getAllMunicipalities = async (): Promise<Municipality[]> => {
  try {
    // Bruk cachen hvis den allerede finnes
    if (allMunicipalitiesCache && allMunicipalitiesCache.length > 0) {
      return allMunicipalitiesCache;
    }
    
    // Import dynamically to avoid circular dependencies
    const { fetchAllMunicipalFees } = await import('@/services/ssbService');
    const data = await fetchAllMunicipalFees();
    
    // If we got data from the API, cache it and return
    if (data && data.length > 0) {
      allMunicipalitiesCache = data;
      return data;
    }
    
    // Fallback to mock data if the API call failed or returned empty data
    console.warn('Using mock data because API call failed or returned empty data');
    allMunicipalitiesCache = mockMunicipalities;
    return mockMunicipalities;
  } catch (error) {
    console.error('Error getting all municipalities:', error);
    allMunicipalitiesCache = mockMunicipalities;
    return mockMunicipalities;
  }
};

export const getMunicipalityById = async (id: string): Promise<Municipality | undefined> => {
  try {
    // Prøv først å finne kommunen i de filtrerte topp/bunn kommunene som alltid har historiske data
    const filteredMunicipalities = await getMunicipalities();
    const filteredMunicipality = filteredMunicipalities.find(m => m.id === id);
    
    if (filteredMunicipality && filteredMunicipality.historicalData.length > 1) {
      return filteredMunicipality;
    }
    
    // Hvis ikke funnet blant filtrerte, sjekk i hele kommunelisten
    const allMunicipalities = await getAllMunicipalities();
    const municipality = allMunicipalities.find(m => m.id === id);
    
    if (municipality) {
      return municipality;
    }
    
    // Hvis ingen match i noen av listene
    console.warn(`Ingen kommune funnet med ID: ${id}`);
    return undefined;
  } catch (error) {
    console.error('Feil ved henting av kommunedetaljer:', error);
    return undefined;
  }
};

export const getHighestFee = async (
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): Promise<{ municipalityName: string, feeAmount: number }> => {
  let highest = { municipalityName: "", feeAmount: 0 };

  try {
    // Bruk alle kommuner for mer nøyaktige statistikker
    const municipalities = await getAllMunicipalities();
    municipalities.forEach(municipality => {
      const total = sumFees(municipality.currentFees, enabledCategories);
      if (total > highest.feeAmount) {
        highest = { municipalityName: municipality.name, feeAmount: total };
      }
    });
  } catch (error) {
    console.error('Error calculating highest fee:', error);
    // Fallback til å bruke filtrerte kommuner hvis vi ikke kan hente alle
    const filteredMunicipalities = await getMunicipalities();
    filteredMunicipalities.forEach(municipality => {
      const total = sumFees(municipality.currentFees, enabledCategories);
      if (total > highest.feeAmount) {
        highest = { municipalityName: municipality.name, feeAmount: total };
      }
    });
  }

  return highest;
};

export const getLowestFee = async (
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): Promise<{ municipalityName: string, feeAmount: number }> => {
  let lowest = { municipalityName: "", feeAmount: Number.MAX_VALUE };

  try {
    // Bruk alle kommuner for mer nøyaktige statistikker
    const municipalities = await getAllMunicipalities();
    municipalities.forEach(municipality => {
      const total = sumFees(municipality.currentFees, enabledCategories);
      if (total < lowest.feeAmount && total > 0) { // Ignorer kommuner med 0 i avgift (sannsynligvis feil)
        lowest = { municipalityName: municipality.name, feeAmount: total };
      }
    });
  } catch (error) {
    console.error('Error calculating lowest fee:', error);
    // Fallback til å bruke filtrerte kommuner hvis vi ikke kan hente alle
    const filteredMunicipalities = await getMunicipalities();
    filteredMunicipalities.forEach(municipality => {
      const total = sumFees(municipality.currentFees, enabledCategories);
      if (total < lowest.feeAmount && total > 0) {
        lowest = { municipalityName: municipality.name, feeAmount: total };
      }
    });
  }

  return lowest;
};

export const getAverageFeeIncrease = async (
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): Promise<number> => {
  let totalIncreasePercent = 0;
  let count = 0;

  try {
    // Bruk alle kommuner for mer nøyaktige statistikker
    const municipalities = await getAllMunicipalities();
    municipalities.forEach(municipality => {
      const histData = municipality.historicalData;
      if (histData.length >= 2) {
        const oldestTotal = getYearlyTotal(histData[0], enabledCategories);
        const newestTotal = getYearlyTotal(histData[histData.length - 1], enabledCategories);

        // Sjekk at vi har gyldige verdier
        if (oldestTotal > 0) {
          const increasePercent = ((newestTotal - oldestTotal) / oldestTotal) * 100;
          // Bare inkluder normale verdier (filtrer ut ekstreme utliggere)
          if (increasePercent > -50 && increasePercent < 200) {
            totalIncreasePercent += increasePercent;
            count++;
          }
        }
      }
    });
  } catch (error) {
    console.error('Error calculating average fee increase:', error);
    // Fallback til å bruke filtrerte kommuner
    const filteredMunicipalities = await getMunicipalities();
    filteredMunicipalities.forEach(municipality => {
      const histData = municipality.historicalData;
      if (histData.length >= 2) {
        const oldestTotal = getYearlyTotal(histData[0], enabledCategories);
        const newestTotal = getYearlyTotal(histData[histData.length - 1], enabledCategories);
        if (oldestTotal > 0) {
          const increasePercent = ((newestTotal - oldestTotal) / oldestTotal) * 100;
          if (increasePercent > -50 && increasePercent < 200) {
            totalIncreasePercent += increasePercent;
            count++;
          }
        }
      }
    });
  }

  return count > 0 ? totalIncreasePercent / count : 0;
};

export interface StatsSummaryData {
  highest: { municipalityName: string; feeAmount: number };
  lowest: { municipalityName: string; feeAmount: number };
  avgIncrease: number;
  avgIncreaseLastYear: number | null; // Snitt økning siste år (år-over-år)
  avgFee: number;
  municipalityCount: number;
}

/** Hent alle nøkkeltall i én kall – mer effektivt enn separate kall */
export const getStatsSummary = async (
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): Promise<StatsSummaryData> => {
  const municipalities = await getAllMunicipalities().catch(() => getMunicipalities());
  let highest = { municipalityName: "", feeAmount: 0 };
  let lowest = { municipalityName: "", feeAmount: Number.MAX_VALUE };
  let totalFees = 0;
  let feeCount = 0;
  let totalIncreasePercent = 0;
  let increaseCount = 0;
  let totalLastYearPct = 0;
  let lastYearCount = 0;

  municipalities.forEach(municipality => {
    const total = sumFees(municipality.currentFees, enabledCategories);
    if (total > 0) {
      feeCount++;
      totalFees += total;
      if (total > highest.feeAmount) highest = { municipalityName: municipality.name, feeAmount: total };
      if (total < lowest.feeAmount) lowest = { municipalityName: municipality.name, feeAmount: total };
    }
    const histData = municipality.historicalData;
    if (histData.length >= 2) {
      const oldestTotal = getYearlyTotal(histData[0], enabledCategories);
      const newestTotal = getYearlyTotal(histData[histData.length - 1], enabledCategories);
      if (oldestTotal > 0) {
        const pct = ((newestTotal - oldestTotal) / oldestTotal) * 100;
        if (pct > -50 && pct < 200) {
          totalIncreasePercent += pct;
          increaseCount++;
        }
      }
    }
    if (histData.length >= 2) {
      const prevTotal = getYearlyTotal(histData[histData.length - 2], enabledCategories);
      const currTotal = getYearlyTotal(histData[histData.length - 1], enabledCategories);
      if (prevTotal > 0) {
        const lastYearPct = ((currTotal - prevTotal) / prevTotal) * 100;
        if (lastYearPct > -50 && lastYearPct < 200) {
          totalLastYearPct += lastYearPct;
          lastYearCount++;
        }
      }
    }
  });

  return {
    highest,
    lowest,
    avgIncrease: increaseCount > 0 ? totalIncreasePercent / increaseCount : 0,
    avgIncreaseLastYear: lastYearCount > 0 ? totalLastYearPct / lastYearCount : null,
    avgFee: feeCount > 0 ? totalFees / feeCount : 0,
    municipalityCount: municipalities.length,
  };
};

// Nye datastrukturer for FAQ og tips
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: 'grunnleggende' | 'beregning' | 'fakturering' | 'besparelse';
}

export interface SavingTip {
  id: string;
  title: string;
  description: string;
  estimatedSaving: string;
  category: 'vann' | 'avløp' | 'renovasjon' | 'septik' | 'generelt';
  difficulty: 'enkel' | 'moderat' | 'avansert';
}

// Vanlige spørsmål om kommunale avgifter
export const commonQuestions: FAQ[] = [
  {
    id: 'q1',
    question: 'Hva er kommunale avgifter?',
    answer: 'Kommunale avgifter er gebyrer som innbyggere betaler til kommunen for tjenester som vann, avløp, renovasjon, septiktømming og feiing. Disse avgiftene skal dekke kommunens kostnader for å levere disse tjenestene.',
    category: 'grunnleggende'
  },
  {
    id: 'q2',
    question: 'Hvordan beregnes kommunale avgifter?',
    answer: 'Kommunale avgifter beregnes basert på selvkostprinsippet, som betyr at kommunen bare kan ta betalt for de faktiske kostnadene ved å levere tjenestene. Avgiftene varierer mellom kommuner basert på faktorer som infrastruktur, geografi, befolkningstetthet og investeringsbehov.',
    category: 'beregning'
  },
  {
    id: 'q3',
    question: 'Hvor ofte må jeg betale kommunale avgifter?',
    answer: 'De fleste kommuner fakturerer kommunale avgifter kvartalsvis (4 ganger i året), men noen kommuner tilbyr månedlig eller halvårlig fakturering. Sjekk med din kommune for spesifikke betalingsplaner.',
    category: 'fakturering'
  },
  {
    id: 'q4',
    question: 'Kan jeg få reduserte kommunale avgifter?',
    answer: 'Enkelte kommuner tilbyr reduserte avgifter til husholdninger med lav inntekt eller spesielle behov. Det kan også være mulig å søke om fritak for tjenester du ikke bruker under visse omstendigheter, for eksempel ved ubebodd bolig.',
    category: 'besparelse'
  },
  {
    id: 'q5',
    question: 'Hvorfor varierer avgiftene så mye mellom kommuner?',
    answer: 'Variasjonene skyldes flere faktorer: geografiske forhold (fjell vs. flatt terreng), befolkningstetthet, alderen på infrastrukturen, investeringsbehov, og hvor effektivt tjenestene drives. Små kommuner med spredt bebyggelse har ofte høyere kostnader per innbygger.',
    category: 'grunnleggende'
  },
  {
    id: 'q6',
    question: 'Hva er selvkostprinsippet?',
    answer: 'Selvkostprinsippet betyr at kommunen bare kan kreve inn avgifter som tilsvarer de faktiske kostnadene ved å levere tjenestene. Kommunen kan ikke tjene penger på disse avgiftene, men skal gå i balanse over tid.',
    category: 'beregning'
  },
  {
    id: 'q7',
    question: 'Hva skjer hvis jeg ikke betaler kommunale avgifter?',
    answer: 'Manglende betaling av kommunale avgifter kan føre til purringer, inkasso og i ytterste konsekvens tvangssalg av boligen. Det er viktig å kontakte kommunen så tidlig som mulig hvis du har betalingsproblemer.',
    category: 'fakturering'
  },
  {
    id: 'q8',
    question: 'Kan jeg få avgiftsfritak for en ubebodd bolig?',
    answer: 'Mange kommuner tilbyr fritak eller reduksjon for ubebodde boliger. Dette må vanligvis søkes om, og det kan være krav om at vanntilførselen stenges for å få fritak for vann- og avløpsavgifter.',
    category: 'besparelse'
  }
];

// Tips for å redusere kommunale avgifter
export const savingTips: SavingTip[] = [
  {
    id: 'tip1',
    title: 'Installere vannbesparende dusjhode',
    description: 'Et vannbesparende dusjhode kan redusere vannforbruket med opptil 50%. Dette reduserer både vann- og avløpsavgifter, samt energikostnader for oppvarming av vann.',
    estimatedSaving: '500-800 kr/år',
    category: 'vann',
    difficulty: 'enkel'
  },
  {
    id: 'tip2',
    title: 'Reparere lekkende kraner og toaletter',
    description: 'En dryppende kran kan sløse bort opptil 20.000 liter vann i året. Et lekkende toalett kan bruke enda mer. Sjekk og reparer lekkasjer regelmessig.',
    estimatedSaving: '400-1200 kr/år',
    category: 'vann',
    difficulty: 'moderat'
  },
  {
    id: 'tip3',
    title: 'Komposter matavfall',
    description: 'Ved å kompostere matavfall kan du redusere mengden avfall som må hentes. Noen kommuner tilbyr rabatter på renovasjonsavgiften for husstander som komposterer.',
    estimatedSaving: '300-600 kr/år',
    category: 'renovasjon',
    difficulty: 'moderat'
  },
  {
    id: 'tip4',
    title: 'Dele renovasjonstjenester med naboer',
    description: 'I noen kommuner kan naboer dele avfallsbeholdere og dele på kostnadene. Dette kan være spesielt effektivt for små husholdninger.',
    estimatedSaving: '500-900 kr/år',
    category: 'renovasjon',
    difficulty: 'enkel'
  },
  {
    id: 'tip5',
    title: 'Installere vannmåler',
    description: 'I mange kommuner beregnes vann- og avløpsavgifter basert på boligareal. Hvis du bruker mindre vann enn gjennomsnittet, kan installasjon av vannmåler redusere avgiftene.',
    estimatedSaving: '1000-2500 kr/år',
    category: 'vann',
    difficulty: 'avansert'
  },
  {
    id: 'tip6',
    title: 'Regelmessig vedlikehold av septiktank',
    description: 'God vedlikehold av septiktanker kan forlenge tiden mellom tømminger og redusere risikoen for kostbare reparasjoner.',
    estimatedSaving: '500-1500 kr/år',
    category: 'septik',
    difficulty: 'moderat'
  },
  {
    id: 'tip7',
    title: 'Samle regnvann til hagevanning',
    description: 'Ved å samle regnvann i tønner kan du redusere vannforbruket betydelig i sommerhalvåret når hagevanning ofte utgjør en stor del av vannforbruket.',
    estimatedSaving: '300-700 kr/år',
    category: 'vann',
    difficulty: 'enkel'
  },
  {
    id: 'tip8',
    title: 'Sjekk om du er berettiget til reduserte avgifter',
    description: 'Noen kommuner tilbyr reduserte avgifter for lavinntektshusholdninger, pensjonister eller personer med spesielle behov. Kontakt kommunen for å finne ut om du kvalifiserer.',
    estimatedSaving: '1000-3000 kr/år',
    category: 'generelt',
    difficulty: 'enkel'
  }
];

// Beregningsmål for miljøpåvirkning
export const environmentalImpact = {
  waterSavingPerPerson: 40000, // liter per år som gjennomsnittsperson bruker
  waterCO2PerLiter: 0.0003, // kg CO2-ekvivalenter per liter vannbehandling
  wasteCO2PerKg: 0.5, // kg CO2-ekvivalenter per kg avfall
  averageWastePerPerson: 450 // kg avfall per person per år
};

// Kalkulatorfunksjoner
export const calculateWaterUsage = (persons: number, savingFactor = 1): number => {
  return persons * environmentalImpact.waterSavingPerPerson * savingFactor;
};

export const calculateWaterCO2 = (waterUsage: number): number => {
  return waterUsage * environmentalImpact.waterCO2PerLiter;
};

export const calculateWasteCO2 = (persons: number, wasteFactor = 1): number => {
  return persons * environmentalImpact.averageWastePerPerson * environmentalImpact.wasteCO2PerKg * wasteFactor;
};

// Ranking-funksjoner
export const calculateMunicipalityRanking = (
  municipalities: Municipality[],
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): Municipality[] => {
  // Sorter kommuner etter totale avgifter (høyest først)
  const sortedMunicipalities = [...municipalities].sort((a, b) => {
    const totalA = sumFees(a.currentFees, enabledCategories);
    const totalB = sumFees(b.currentFees, enabledCategories);
    return totalB - totalA; // Høyest først
  });

  // Tildel ranking (1 = dyreste)
  return sortedMunicipalities.map((municipality, index) => ({
    ...municipality,
    ranking: index + 1
  }));
};

export const getMunicipalityRanking = (
  municipality: Municipality,
  allMunicipalities: Municipality[],
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): number => {
  const sortedMunicipalities = [...allMunicipalities].sort((a, b) => {
    const totalA = sumFees(a.currentFees, enabledCategories);
    const totalB = sumFees(b.currentFees, enabledCategories);
    return totalB - totalA; // Høyest først
  });

  const index = sortedMunicipalities.findIndex(m => m.id === municipality.id);
  return index !== -1 ? index + 1 : 0;
};

/** Ranking for et spesifikt år (basert på historiske data) */
export const getMunicipalityRankingForYear = (
  municipality: Municipality,
  allMunicipalities: Municipality[],
  year: number,
  enabledCategories: string[] = DEFAULT_ENABLED_CATEGORIES
): number => {
  const getTotal = (m: Municipality) => {
    const yearData = getYearlyDataForYear(m, year);
    return yearData ? getYearlyTotal(yearData, enabledCategories) : 0;
  };
  const sortedMunicipalities = [...allMunicipalities].sort((a, b) => getTotal(b) - getTotal(a));
  const index = sortedMunicipalities.findIndex(m => m.id === municipality.id);
  return index !== -1 ? index + 1 : 0;
};
